import { useState } from 'react'
import { useSelector } from 'react-redux'

import type { CommentTopic } from 'services/content/types'
import type {
  Comment as CommentType,
  PostCommentRequestBody,
} from 'services/comments/types'
import CommentForm from '../CommentForm'
import { Comment } from 'ui-component-library/components/CommentsModule'
import { getCommentStrings } from 'store/strings/selectors'
import { getUserId } from 'store/user/selectors'
import copyURLToClipboard from 'utilities/copyURLtoClipboard'

type Props = {
  comment: CommentType
  topic?: CommentTopic
  onSubmitReply?: (post: PostCommentRequestBody) => Promise<void>
  onSubmitEdit: (post: PostCommentRequestBody) => Promise<void>
  children?: React.ReactNode
}

function CommentContainer({
  comment,
  topic,
  onSubmitReply,
  onSubmitEdit,
  children,
}: Props) {
  const {
    editLabelText,
    replyToText,
    editButtonText,
    editedText,
    shareText,
    commentUrlCopiedTooltipText,
    printText,
    formText,
    closeFormLabelText,
    commentedOnText,
  } = useSelector(getCommentStrings)
  const userId = useSelector(getUserId)

  const [showReplyForm, setShowReplyForm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const canEdit = userId === comment.author.userId

  function handleToggleShowReplyForm() {
    setShowReplyForm((s) => !s)
  }

  function handleToggleShowEditForm() {
    setIsEditing((s) => !s)
  }

  function handleShare() {
    copyURLToClipboard(`${window.location.href}#comment-${comment.id}`)
  }

  const handleSubmitReply = onSubmitReply
    ? (text: string) => {
        return onSubmitReply({ text, id: comment.id })
      }
    : undefined

  function handleSubmitEdit(text: string) {
    return onSubmitEdit({ text, id: comment.id })
  }

  return (
    <Comment
      topic={topic}
      textLabelText={editLabelText}
      editButtonText={editButtonText}
      shareButtonText={shareText}
      shareTooltipText={commentUrlCopiedTooltipText}
      printButtonText={printText}
      replyButtonText={replyToText}
      editedText={editedText}
      commentedOnText={commentedOnText}
      onToggleEditForm={canEdit ? handleToggleShowEditForm : undefined}
      onSubmitEdit={handleSubmitEdit}
      onReply={onSubmitReply && handleToggleShowReplyForm}
      onShare={handleShare}
      comment={comment}
      closeFormLabelText={closeFormLabelText}
      submitButtonText={formText.edit.buttonText}
      isEditing={isEditing}
      replyForm={
        showReplyForm &&
        handleSubmitReply && (
          <CommentForm
            onSubmit={handleSubmitReply}
            buttonText={formText.reply.buttonText}
            placeholderText={formText.reply.placeholderText}
            onClose={handleToggleShowReplyForm}
          />
        )
      }
    >
      {children}
    </Comment>
  )
}

export default CommentContainer
